/* Exhibition wall (sections/vvb-global-presence.liquid). Builds the square wall from the JSON the section prints.
   Desktop frame (12 columns): a 4x4 card in the middle and a 4x4 block of squares on each side. A side holds 1 photo (4x4), 4 (2x2 each),
   7, 10, 13 or 16 (mix of 2x2 and 1x1), so every show fills the same frame whatever its photo count; the first photos become the large squares.
   "All" adds one row of small squares above and below. Phones: the card on top, photos below in rows that are always full.
   No text on the photos; the show name appears only in the viewer. */
(() => {
  const seen = new WeakSet();
  const SIDE = [1, 4, 7, 10, 13, 16];
  const CAP = 20;

  function init(root) {
    if (!root || seen.has(root)) return;
    seen.add(root);
    const dataEl = root.querySelector('[data-xw-data]'), wall = root.querySelector('[data-xw-wall]'), tabs = root.querySelector('[data-xw-tabs]');
    let D; try { D = JSON.parse(dataEl.textContent); } catch (e) { return; }
    const ev = Object.fromEntries(D.events.map(e => [e.key, e]));
    const card35 = root.querySelector('[data-xw-card]');
    const dlg = root.querySelector('[data-xw-dialog]');
    let set = [], idx = 0, mode = 'all';

    // unique photos (same image listed twice is shown once), shows interleaved for the mixed wall
    const uniq = []; const seenSrc = new Set();
    D.photos.forEach(p => { const k = p.big.split('?')[0]; if (!seenSrc.has(k)) { seenSrc.add(k); uniq.push(p); } });
    const mix = (() => {
      const by = {}, keys = [];
      uniq.filter(p => !p.only).forEach(p => { if (!by[p.ev]) { by[p.ev] = []; keys.push(p.ev); } by[p.ev].push(p); });
      const out = []; let i = 0, more = true;
      while (more) { more = false; keys.forEach(k => { if (by[k][i]) { out.push(by[k][i]); more = true; } }); i++; }
      return out;
    })();

    const desktop = () => wall.clientWidth >= 700;
    const size = cols => { const w = wall.clientWidth, g = 6; wall.style.setProperty('--cols', cols); wall.style.setProperty('--cell', ((w - g * (cols - 1)) / cols) + 'px'); };
    const place = (el, c, r, w, h) => { el.style.gridColumn = c + ' / span ' + w; el.style.gridRow = r + ' / span ' + h; };
    const tile = (p, i) => {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'xw-sq'; b.style.animationDelay = Math.min(i * 18, 700) + 'ms';
      b.setAttribute('aria-label', D.enlarge || 'Enlarge exhibition photo');
      b.innerHTML = '<img alt="" loading="lazy" decoding="async" src="' + p.sq + '" width="420" height="420">';
      b.addEventListener('click', () => open(set.indexOf(p)));
      return b;
    };
    const t = s => String(s || '').replace(/[&<>"]/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[m]));
    const cardEv = e => {
      const c = document.createElement('div');
      c.className = 'xw-card ev' + (e.next ? ' next' : '');
      c.innerHTML = '<p class="xw-k">' + t(e.next ? D.next : D.archive) + '</p><h3>' + t(e.name) + '</h3><div class="xw-pl">' + t(e.place) + (e.year ? ' · ' + t(e.year) : '') + '</div>' +
        (e.note ? '<div class="xw-nt">' + t(e.note) + '</div>' : '') + '<span class="xw-rule"></span><p class="xw-msg">' + t(D.tagline) + '</p>';
      return c;
    };

    /* one 4x4 side starting at column c0, row r0, holding `count` photos from list (bigs first) */
    function side(list, count, c0, r0, mirror, i0) {
      if (count === 1) { const el = tile(list[0], i0); place(el, c0, r0, 4, 4); wall.appendChild(el); return; }
      const bigs = (16 - count) / 3, smalls = count - bigs;
      const slots = mirror ? [[2, 0], [0, 2], [0, 0], [2, 2]] : [[0, 0], [2, 2], [2, 0], [0, 2]];
      const used = new Set();
      for (let b = 0; b < bigs; b++) {
        const [x, y] = slots[b]; const el = tile(list[b], i0 + b); place(el, c0 + x, r0 + y, 2, 2); wall.appendChild(el);
        [[0, 0], [1, 0], [0, 1], [1, 1]].forEach(([dx, dy]) => used.add((x + dx) + ',' + (y + dy)));
      }
      let k = bigs;
      for (let y = 0; y < 4; y++) for (let x = 0; x < 4; x++) {
        if (used.has(x + ',' + y) || k >= bigs + smalls) continue;
        const el = tile(list[k], i0 + k); place(el, c0 + x, r0 + y, 1, 1); wall.appendChild(el); k++;
      }
    }
    /* best split of n photos into one or two sides */
    function split(n) {
      // two sides whenever possible (same frame for every show); one side only when a show has a single photo
      let best = null;
      SIDE.forEach(a => SIDE.forEach(b => { const s = a + b; if (s <= Math.min(n, CAP) && (!best || s > best.s || (s === best.s && Math.abs(a - b) < Math.abs(best.a - best.b)))) best = { s, a, b }; }));
      if (!best && n >= 1) best = { s: 1, a: 1, b: 0 };
      return best;
    }
    function frame(list, r0, card) {
      const sp = split(list.length);
      if (!sp) { place(card, 5, r0, 4, 4); wall.appendChild(card); return []; }
      const twoSides = sp.b > 0;
      const cardCol = twoSides ? 5 : 3, firstCol = twoSides ? 1 : 7;
      place(card, cardCol, r0, 4, 4); wall.appendChild(card);
      // large squares alternate between the sides, so both sides get the best photos
      const nb = c => (c === 1 ? 1 : (16 - c) / 3);
      const bigA = nb(sp.a), bigB = sp.b ? nb(sp.b) : 0;
      const pool = list.slice(0, sp.s), aBig = [], bBig = [];
      pool.slice(0, bigA + bigB).forEach((p, i) => ((i % 2 === 0 && aBig.length < bigA) || bBig.length >= bigB ? aBig : bBig).push(p));
      const rest = pool.slice(bigA + bigB);
      const A = aBig.concat(rest.slice(0, sp.a - bigA)), B = bBig.concat(rest.slice(sp.a - bigA));
      side(A, sp.a, firstCol, r0, false, 0);
      if (twoSides) side(B, sp.b, 9, r0, true, sp.a);
      return A.concat(B);
    }

    function show(k) {
      mode = k;
      tabs.querySelectorAll('.xw-tab').forEach(b => b.setAttribute('aria-pressed', String(b.dataset.k === k)));
      wall.replaceChildren();
      const list = k === 'all' ? mix : uniq.filter(p => p.ev === k);
      const card = k === 'all' ? card35 : cardEv(ev[k]);
      if (desktop()) {
        size(12);
        if (k === 'all') {
          const top = list.slice(0, 12), bottom = list.slice(12, 24), mid = list.slice(24);
          top.forEach((p, i) => { const el = tile(p, i); place(el, i + 1, 1, 1, 1); wall.appendChild(el); });
          const used = frame(mid, 2, card);
          bottom.forEach((p, i) => { const el = tile(p, i); place(el, i + 1, 6, 1, 1); wall.appendChild(el); });
          set = top.concat(used, bottom);
        } else {
          set = frame(list, 1, card);
        }
      } else {
        size(6);
        let r = 1; set = [];
        if (k === 'all') { list.slice(0, 6).forEach((p, i) => { const el = tile(p, i); place(el, i + 1, 1, 1, 1); wall.appendChild(el); set.push(p); }); r = 2; }
        place(card, 1, r, 6, 6); wall.appendChild(card); r += 6;
        const rest = k === 'all' ? list.slice(6, 15) : list.slice(0, 12);
        const n = rest.length;
        if (n === 1) { const el = tile(rest[0], 0); place(el, 1, r, 6, 6); wall.appendChild(el); set.push(rest[0]); }
        else if (n === 2 || n === 4) rest.forEach((p, i) => { const el = tile(p, i); place(el, 1 + (i % 2) * 3, r + Math.floor(i / 2) * 3, 3, 3); wall.appendChild(el); set.push(p); });
        else { const m = n - (n % 3); rest.slice(0, m).forEach((p, i) => { const el = tile(p, i); place(el, 1 + (i % 3) * 2, r + Math.floor(i / 3) * 2, 2, 2); wall.appendChild(el); set.push(p); }); }
      }
    }
    function open(i) {
      if (!set.length || i < 0) return;
      idx = (i + set.length) % set.length;
      const p = set[idx], e = ev[p.ev] || {};
      dlg.querySelector('[data-xw-big]').src = p.big;
      dlg.querySelector('[data-xw-cap]').textContent = [e.name, e.place, e.year].filter(Boolean).join(' · ');
      if (!dlg.open) dlg.showModal();
    }
    tabs.querySelectorAll('.xw-tab').forEach(b => b.addEventListener('click', () => show(b.dataset.k)));
    dlg.querySelector('[data-xw-prev]').addEventListener('click', () => open(idx - 1));
    dlg.querySelector('[data-xw-next]').addEventListener('click', () => open(idx + 1));
    dlg.querySelector('[data-xw-close]').addEventListener('click', () => dlg.close());
    dlg.addEventListener('keydown', e => { if (e.key === 'ArrowLeft') open(idx - 1); if (e.key === 'ArrowRight') open(idx + 1); });
    dlg.addEventListener('click', e => { if (e.target === dlg) dlg.close(); });
    let last = desktop(), tm = 0;
    addEventListener('resize', () => { clearTimeout(tm); tm = setTimeout(() => { const d = desktop(); size(d ? 12 : 6); if (d !== last) { last = d; show(mode); } }, 150); });
    show('all');
  }
  const run = scope => (scope || document).querySelectorAll('[data-xw]').forEach(init);
  run();
  document.addEventListener('shopify:section:load', e => run(e.target));
})();
